import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useRun } from "./use-run";
import { rendererFor } from "./renderers/registry";
import { deleteRun } from "@/api/runs";
import type { RunStepRead } from "@/api/create-types";
import { Alert, Button, Spinner } from "@/components/ui";

function StepRow({ step }: { step: RunStepRead }) {
  const icon =
    step.status === "complete"
      ? "✓"
      : step.status === "failed"
        ? "✕"
        : step.status === "running"
          ? "•"
          : step.status === "awaiting_user"
            ? "✎"
            : "·";
  const tone =
    step.status === "complete"
      ? "bg-emerald-100 text-emerald-700"
      : step.status === "failed"
        ? "bg-red-100 text-red-700"
        : step.status === "running"
          ? "bg-brand-100 text-brand-700 animate-pulse"
          : "bg-black/5 text-ink-faint";
  return (
    <div className="flex items-center gap-3">
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${tone}`}>
        {icon}
      </span>
      <span className="text-sm text-ink-soft">{step.part_slug}</span>
    </div>
  );
}

export function RunPage() {
  const { t } = useTranslation();
  const { runId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { query, advance } = useRun(runId);
  const run = query.data;

  const del = useMutation({
    mutationFn: () => deleteRun(runId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["active-runs"] });
      navigate("/app/create");
    },
  });
  const cancel = () => {
    if (window.confirm(t("Cancel this generation and delete the run?"))) del.mutate();
  };

  // The PDF-marking step needs a wide canvas; everything else stays narrow.
  const activeStep =
    run?.status === "awaiting_user"
      ? run.steps.find((s) => s.status === "awaiting_user") ?? run.steps[run.current_step_index]
      : undefined;
  const wide = activeStep?.part_slug === "lecture.marking";

  const advanceError = advance.isError
    ? advance.error instanceof Error
      ? advance.error.message
      : t("Couldn't submit — please try again.")
    : null;

  return (
    <div className={`mx-auto ${wide ? "max-w-screen-2xl" : "max-w-2xl"} animate-fade-up`}>
      <div className="flex items-center justify-between">
        <Link to="/app/create" className="text-sm text-brand-600 hover:underline">
          {t("← Create")}
        </Link>
        {run && run.status !== "complete" && (
          <button
            onClick={cancel}
            disabled={del.isPending}
            className="text-sm text-red-500 hover:underline disabled:opacity-50"
          >
            {run.status === "failed" ? t("Delete run") : t("Cancel run")}
          </button>
        )}
      </div>

      {query.isLoading && (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {query.isError && (
        <div className="mt-6">
          <Alert>{query.error instanceof Error ? query.error.message : t("Run not found.")}</Alert>
        </div>
      )}

      {run && (
        <div className="mt-4">
          {/* awaiting_user → the step renderer */}
          {run.status === "awaiting_user" &&
            (() => {
              const step =
                run.steps.find((s) => s.status === "awaiting_user") ??
                run.steps[run.current_step_index];
              if (!step) return <Alert>{t("Waiting for the next step…")}</Alert>;
              const Renderer = rendererFor(step.part_slug);
              return (
                <Renderer
                  run={run}
                  step={step}
                  submitting={advance.isPending}
                  error={advanceError}
                  onAdvance={(resp) => advance.mutate(resp)}
                />
              );
            })()}

          {/* in-flight → progress */}
          {(run.status === "pending" || run.status === "running") && (
            <div className="py-8 text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                <Spinner className="h-7 w-7" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{t("Eddi is working…")}</h1>
              <p className="mt-1 text-ink-faint">
                {t("Generating your set. This can take a minute — you can leave this page and come back.")}
              </p>
              <div className="card mx-auto mt-6 max-w-sm space-y-2 p-5 text-left">
                {run.steps.map((s) => (
                  <StepRow key={s.index} step={s} />
                ))}
              </div>
            </div>
          )}

          {/* complete → success */}
          {run.status === "complete" && (
            <div className="py-10 text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-3xl">
                🎉
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{t("Your set is ready")}</h1>
              <p className="mt-1 text-ink-faint">
                {t("Eddi finished generating. Open your Library to start practising.")}
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/app/library">
                  <Button size="lg">{t("Go to Library")}</Button>
                </Link>
                <Link to="/app/create">
                  <Button variant="secondary" size="lg">
                    {t("Create another")}
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* failed → error */}
          {run.status === "failed" && (
            <div className="py-10">
              <div className="mx-auto max-w-md text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-100 text-3xl">
                  ⚠️
                </div>
                <h1 className="text-2xl font-bold tracking-tight">{t("Generation failed")}</h1>
                <p className="mt-1 text-ink-faint">
                  {run.error_message || t("Something went wrong while generating this set.")}
                </p>
                <div className="mt-6">
                  <Link to="/app/create">
                    <Button size="lg">{t("Back to Create")}</Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
